import React, { useCallback, useEffect, useState } from "react";
import { CardDetail, ManageApplicationCardControlled } from "./card-controlled";
import {
  ApplicationManageData,
  JobScheduleDetails,
  SdaInfo,
} from "../../utility/application-data";
import { getSupportedLanguage } from "../../utility/locale-helper";
import useArb from "../../hooks/use-arb";
import { getProcessedJCAddress } from "../../utility/jc-response-helper";

interface ManageApplicationCardUncontrolledProps {
  jcAccessor: () => Promise<JobScheduleDetails | undefined>;
  sdaAccessor: () => Promise<SdaInfo | undefined>;
  applicationManageAccessor: () => Promise<ApplicationManageData | undefined>;
  trackScheduleChangeClick: () => void;
  trackWithdrawApplicationClick: () => void;
  trackWithdrawApplicationConfirmButtonClick: (withdrawReason: string) => void;
  cssURL: string;
  dashboardURL: string;
  applicationId: string;
  collapsed: boolean;
}

export const ManageApplicationCardUncontrolled = ({
  applicationId,
  jcAccessor,
  sdaAccessor,
  applicationManageAccessor,
  trackScheduleChangeClick,
  trackWithdrawApplicationClick,
  trackWithdrawApplicationConfirmButtonClick,
  cssURL,
  dashboardURL,
  collapsed,
}: ManageApplicationCardUncontrolledProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [scheduleDetails, setScheduleDetails] = useState<
    JobScheduleDetails | undefined
  >();
  const [eligibleForCSS, setEligibleForCSS] = useState<boolean>(false);
  const [eligibleForWithdraw, setEligibleForWithdraw] = useState<boolean>(true);
  const [startDateDetails, setStartDateDetails] = useState<
    SdaInfo | undefined
  >();
  const [scheduleIdFound, setScheduleIdFound] = useState<boolean>(true);
  const bundle = useArb("HVHChecklistPantherResource");

  const requestJCAndSDA = useCallback(async () => {
    setLoading(true);
    try {
      const scheduleDetails = await jcAccessor();
      const startDateDetails = await sdaAccessor();
      const applicationManageDetails = await applicationManageAccessor();

      if (scheduleDetails?.error) {
        if (
          scheduleDetails?.error?.errorCode === "APPLICATION_DETAILS_NOT_FOUND"
        ) {
          setScheduleIdFound(false);
        }
        setError(true);
      } else {
        setScheduleDetails(scheduleDetails);
        setError(false);
      }

      if (startDateDetails?.errorCode) {
        setError(true);
      } else {
        setStartDateDetails(startDateDetails);
        setError(false);
      }

      if (applicationManageDetails?.enableCSS) {
        setEligibleForCSS(true);
      }
      if (applicationManageDetails?.hideWithdrawApplication) {
        setEligibleForWithdraw(false);
      }
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    requestJCAndSDA();
  }, []);

  const address = scheduleDetails && getProcessedJCAddress(scheduleDetails);

  const details: CardDetail = {
    jobTitle: scheduleDetails?.externalJobTitle as string,
    hours: scheduleDetails?.scheduleText as string,
    startDate: scheduleDetails?.firstDayOnSiteL10N as string,
    startTime: scheduleDetails?.startTimeL10N as string,
    description: scheduleDetails?.briefJobDescription as string,
    payRate: scheduleDetails?.totalPayRateL10N as string,
    address: address as string,
    image: scheduleDetails?.image as string,
    startDateStatus: startDateDetails?.startDateStatus as string,
    signOnBonus: scheduleDetails?.signOnBonusL10N as string,
    scheduleBannerText: scheduleDetails?.scheduleBannerText as string,
    hoursPerWeek: scheduleDetails?.hoursPerWeekL10N as string,
    employmentTypeL10N: scheduleDetails?.employmentTypeL10N as string,
    supportedLanguage: getSupportedLanguage(
      scheduleDetails?.requiredLanguage,
      bundle
    ) as string,
    parsedTrainingDate: scheduleDetails?.parsedTrainingDate as string,
    monthlyBasePayL10N: scheduleDetails?.monthlyBasePayL10N as string,
    currencyCode: scheduleDetails?.currencyCode as string,
  };

  return (
    <ManageApplicationCardControlled
      applicationId={applicationId}
      details={details}
      onRetry={requestJCAndSDA}
      loading={loading}
      error={error}
      eligibleForCSS={eligibleForCSS}
      eligibleForWithdraw={eligibleForWithdraw}
      scheduleIdFound={scheduleIdFound}
      dashboardURL={dashboardURL}
      cssURL={cssURL}
      onChangeSchedule={trackScheduleChangeClick}
      onWithdrawApplication={trackWithdrawApplicationClick}
      onWithdrawApplicationConfirm={trackWithdrawApplicationConfirmButtonClick}
      collapsed={collapsed}
    />
  );
};
